import { useState } from 'react';
import type { FormEvent } from 'react';
import { ALL_CAPABILITIES, CAPABILITY_SHORT_LABELS, api } from '../api';
import type { AccessToken, AccessTokenAccess, CreatedAccessToken, PermissionCapability } from '../api';
import { useResource } from '../hooks';
import { Callout, Checkbox, Dialog, Input, Section, Select, Table } from './primitives';
import { ConfirmDialog, ErrorNotice, Field, SkeletonPage, StatusBadge } from './ui';
import { CopyButton } from './CopyButton';
import { Timestamp, exactTime } from './status/StatusPieces';

const ACCESS_LABELS: Record<AccessTokenAccess, string> = {
  read: 'Read',
  operate: 'Operate',
  admin: 'Admin',
};

const ACCESS_CHOICES: { value: AccessTokenAccess; label: string }[] = [
  { value: 'read', label: 'Read: view only' },
  { value: 'operate', label: 'Operate: also make the changes you allow below' },
  { value: 'admin', label: 'Admin: everything you can do, deletions included' },
];

const LIFETIMES: { days: number; label: string }[] = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
];

const EXPIRING_SOON_MS = 7 * 24 * 60 * 60 * 1000;

function tokenState(token: AccessToken, now = Date.now()): 'active' | 'expiring' | 'expired' {
  const left = Date.parse(token.expires_at) - now;
  if (left <= 0) return 'expired';
  return left < EXPIRING_SOON_MS ? 'expiring' : 'active';
}

function mcpUrl(): string {
  return `${window.location.origin}/api/v1/mcp`;
}

/** The command that connects Claude Code to this installation. */
export function mcpAddCommand(secret: string): string {
  return `claude mcp add --transport http commerce-ops ${mcpUrl()} --header "Authorization: Bearer ${secret}"`;
}

function TokenName({ token }: { token: AccessToken }) {
  return (
    <span className="cell-stack">
      <span>{token.name}</span>
      <span className="cell-stack__secondary input--mono">cop_{token.display_prefix}…</span>
    </span>
  );
}

function AccessCell({ token }: { token: AccessToken }) {
  const capabilities = token.capabilities.map((c) => CAPABILITY_SHORT_LABELS[c] ?? c);
  return (
    <span className="cell-stack">
      <span className={`badge ${token.access === 'admin' ? 'badge--warning' : 'badge--neutral'}`}>{ACCESS_LABELS[token.access]}</span>
      {token.access === 'operate' ? (
        <span className="cell-stack__secondary">{capabilities.length > 0 ? capabilities.join(', ') : 'No changes'}</span>
      ) : null}
    </span>
  );
}

const expiryDate = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function ExpiryCell({ token }: { token: AccessToken }) {
  const state = tokenState(token);
  return (
    <span className="cell-stack">
      <time dateTime={token.expires_at} title={exactTime(token.expires_at)}>
        {expiryDate.format(new Date(token.expires_at))}
      </time>
      {state !== 'active' ? <StatusBadge kind="accessToken" value={state} /> : null}
    </span>
  );
}

/** A member's own tokens: Account page. */
export function AccessTokensSection({
  isOwner,
  capabilities,
}: {
  isOwner: boolean;
  capabilities: PermissionCapability[];
}) {
  const tokens = useResource(() => api.listAccessTokens(), [], undefined, { key: 'account:access-tokens' });
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedAccessToken | null>(null);
  const [revoking, setRevoking] = useState<AccessToken | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const list = tokens.data?.tokens ?? [];
  const grantable = isOwner ? ALL_CAPABILITIES : capabilities;

  async function revoke(action: () => Promise<unknown>, close: () => void) {
    setBusy(true);
    setError(null);
    try {
      await action();
      tokens.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
      close();
    }
  }

  return (
    <Section
      title="Access tokens"
      testId="account-access-tokens"
      actions={
        <>
          {list.length > 1 ? (
            <button type="button" className="button button--secondary" onClick={() => setRevokingAll(true)}>
              Revoke all
            </button>
          ) : null}
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              setCreated(null);
              setCreateOpen(true);
            }}
          >
            Create token
          </button>
        </>
      }
    >
      {tokens.loading && !tokens.data ? <SkeletonPage rows={2} /> : null}
      <ErrorNotice error={error ?? tokens.error ?? null} />
      {tokens.data ? (
        list.length === 0 ? (
          <p className="quiet-empty">No access tokens</p>
        ) : (
          <Table label="Access tokens" wrapperClassName="table-scroll--shadow-room">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Access</th>
                <th scope="col">Last used</th>
                <th scope="col">Expires</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((token) => (
                <tr key={token.id}>
                  <th scope="row">
                    <TokenName token={token} />
                  </th>
                  <td>
                    <AccessCell token={token} />
                  </td>
                  <td>{token.last_used_at ? <Timestamp iso={token.last_used_at} /> : <span className="muted">Never</span>}</td>
                  <td>
                    <ExpiryCell token={token} />
                  </td>
                  <td className="team__action">
                    <button
                      type="button"
                      className="button button--danger-quiet button--small"
                      aria-label={`Revoke ${token.name}`}
                      onClick={() => setRevoking(token)}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      ) : null}

      <CreateTokenDialog
        open={createOpen}
        created={created}
        grantable={grantable}
        onCreated={(result) => {
          setCreated(result);
          tokens.reload();
        }}
        onClose={() => {
          setCreateOpen(false);
          setCreated(null);
        }}
      />

      <ConfirmDialog
        open={revoking !== null}
        title="Revoke access token"
        description={
          revoking
            ? `${revoking.name} (cop_${revoking.display_prefix}…) stops working at once. Clients using it must be given a new token.`
            : ''
        }
        confirmLabel="Revoke"
        danger
        busy={busy}
        onCancel={() => setRevoking(null)}
        onConfirm={() => revoking && revoke(() => api.revokeAccessToken(revoking.id), () => setRevoking(null))}
      />
      <ConfirmDialog
        open={revokingAll}
        title="Revoke all access tokens"
        description={`All ${list.length} tokens stop working at once.`}
        confirmLabel="Revoke all"
        danger
        busy={busy}
        onCancel={() => setRevokingAll(false)}
        onConfirm={() => revoke(() => api.revokeAllAccessTokens(), () => setRevokingAll(false))}
      />
    </Section>
  );
}

function CreateTokenDialog({
  open,
  created,
  grantable,
  onCreated,
  onClose,
}: {
  open: boolean;
  created: CreatedAccessToken | null;
  grantable: PermissionCapability[];
  onCreated: (result: CreatedAccessToken) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [access, setAccess] = useState<AccessTokenAccess>('read');
  const [selected, setSelected] = useState<Set<PermissionCapability>>(new Set());
  const [days, setDays] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  function reset() {
    setName('');
    setAccess('read');
    setSelected(new Set());
    setDays(30);
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.createAccessToken({
        name: name.trim(),
        access,
        capabilities: access === 'operate' ? grantable.filter((c) => selected.has(c)) : [],
        expires_at: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
      });
      onCreated(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      size="sm"
      title={created ? 'Token created' : 'Create access token'}
      onClose={close}
      busy={submitting}
      onSubmit={created ? undefined : submit}
      testId="access-token-dialog"
      footer={
        created ? (
          <button type="button" className="button button--primary" onClick={close}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="button button--secondary" onClick={close} disabled={submitting}>
              Cancel
            </button>
            <button className="button button--primary" type="submit" disabled={submitting || !name.trim()}>
              {submitting ? 'Creating' : 'Create token'}
            </button>
          </>
        )
      }
    >
      {created ? (
        <div className="stack">
          <Callout tone="warning" title="Copy it now">
            This is the only time the token is shown.
          </Callout>
          <Field label="Token" action={<CopyButton value={created.secret} label="access token" />}>
            <Input readOnly value={created.secret} className="input--mono" onFocus={(e) => e.target.select()} />
          </Field>
          <Field label="Connect Claude Code" action={<CopyButton value={mcpAddCommand(created.secret)} label="connect command" />}>
            <Input readOnly value={mcpAddCommand(created.secret)} className="input--mono" onFocus={(e) => e.target.select()} />
          </Field>
        </div>
      ) : (
        <>
          <Field label="Name">
            <Input required data-autofocus value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Claude Code on my laptop" />
          </Field>
          <Field label="Access">
            <Select value={access} onChange={(e) => setAccess(e.target.value as AccessTokenAccess)}>
              {ACCESS_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </Select>
          </Field>
          {access === 'operate' ? (
            <div className="field" role="group" aria-labelledby="token-capabilities-label">
              <span id="token-capabilities-label" className="field__label">
                May change
              </span>
              {grantable.length === 0 ? (
                <p className="muted">Your role has no permissions to pass on</p>
              ) : (
                <div className="checkbox-group">
                  {grantable.map((capability) => (
                    <Checkbox
                      key={capability}
                      checked={selected.has(capability)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(capability);
                        else next.delete(capability);
                        setSelected(next);
                      }}
                      label={CAPABILITY_SHORT_LABELS[capability]}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : null}
          {access === 'admin' ? (
            <Callout tone="warning">Admin tokens can do anything you can do.</Callout>
          ) : null}
          <Field label="Expires after">
            <Select value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
              {LIFETIMES.map((lifetime) => (
                <option key={lifetime.days} value={lifetime.days}>
                  {lifetime.label}
                </option>
              ))}
            </Select>
          </Field>
          <ErrorNotice error={error} />
        </>
      )}
    </Dialog>
  );
}

/** Every member's tokens: Team page, owners only. */
export function OrganizationAccessTokensSection() {
  const tokens = useResource(() => api.listOrganizationAccessTokens(), [], undefined, { key: 'team:access-tokens' });
  const [revoking, setRevoking] = useState<AccessToken | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const list = tokens.data?.tokens ?? [];

  async function confirmRevoke() {
    if (!revoking) return;
    setBusy(true);
    setError(null);
    try {
      await api.revokeOrganizationAccessToken(revoking.id);
      tokens.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
      setRevoking(null);
    }
  }

  if (tokens.loading && !tokens.data) {
    return (
      <Section title="Access tokens">
        <SkeletonPage rows={2} />
      </Section>
    );
  }
  return (
    <Section title={`Access tokens (${list.length})`} testId="team-access-tokens">
      <ErrorNotice error={error ?? tokens.error ?? null} />
      {list.length === 0 ? (
        <p className="quiet-empty">No access tokens</p>
      ) : (
        <Table label="Access tokens of the organization" wrapperClassName="table-scroll--shadow-room">
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Token</th>
              <th scope="col">Access</th>
              <th scope="col">Last used</th>
              <th scope="col">Expires</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((token) => (
              <tr key={token.id}>
                <th scope="row">
                  <span className="cell-stack">
                    <span>{token.owner_name}</span>
                    <span className="cell-stack__secondary">{token.owner_email}</span>
                  </span>
                </th>
                <td>
                  <TokenName token={token} />
                </td>
                <td>
                  <AccessCell token={token} />
                </td>
                <td>{token.last_used_at ? <Timestamp iso={token.last_used_at} /> : <span className="muted">Never</span>}</td>
                <td>
                  <ExpiryCell token={token} />
                </td>
                <td className="team__action">
                  <button
                    type="button"
                    className="button button--danger-quiet button--small"
                    aria-label={`Revoke ${token.name} of ${token.owner_name ?? 'member'}`}
                    onClick={() => setRevoking(token)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <ConfirmDialog
        open={revoking !== null}
        title="Revoke access token"
        description={
          revoking ? `${revoking.name} of ${revoking.owner_name ?? 'this member'} stops working at once.` : ''
        }
        confirmLabel="Revoke"
        danger
        busy={busy}
        onCancel={() => setRevoking(null)}
        onConfirm={confirmRevoke}
      />
    </Section>
  );
}
